import { SaleItemState, SaleOrder, SaleOrderAdd, SaleOrderAddItem, SaleOrderItemState } from "@/types/sales";
import { App, Button, DatePicker, Flex, Input, TableProps, Tooltip } from "antd";
import { FC, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import useDataList from "@/hooks/useDataList";
import api, { ApprovalListProps, UpdateByKey } from "@/apis/order";
import { CommonStateEnum } from "@/types/common";
import SText from "@/styles/SText";
import dayjs, { Dayjs } from "dayjs";
import useDataUpdate from "@/hooks/useDataUpdate";
import STableLayout, { DEFAULT_PAGE_SIZE, TableLayoutRef } from "@/styles/STableLayout";
import { ColumnsType } from "antd/lib/table";
import { AnyObject } from "antd/lib/_util/type";
import OrderForm from "../../components/OrderForm";
import { isEqualVal } from "@/utils/dataDeal";
import usePassed from "../../hooks/usePassed";
import { dateFormat } from "@/utils/format";
import SelectCustomer from "../../../../components/Selector/SelectCustomer";
import { SearchOutlined } from "@ant-design/icons";
import { useSelector } from "react-redux";
import { RootState } from "@/store";
const { RangePicker } = DatePicker;

const DataReviews: FC = () => {
  const { t } = useTranslation();
  const { notification } = App.useApp();

  const tableRef = useRef<TableLayoutRef | null>(null);
  const [list, total, listLoading, { fetchList, rmItem, updateItem }] = useDataList<SaleItemState, [ApprovalListProps]>({ fetchApi: api.approvalList });
  const { update: updateByKey, loading: updateLoading } = useDataUpdate<[string, UpdateByKey]>({ api: api.updateByKey });
  const { update, loading: updateSaleLoading } = useDataUpdate<[string, SaleOrderAdd], SaleOrderItemState>({ api: api.update });
  const { update: updateSaleItem, loading: updateItemLoading } = useDataUpdate<[string, SaleOrderAddItem]>({ api: api.itemUpdate });

  const [keyword, setKeyword] = useState<string>("");
  const [customerId, setCustomerId] = useState<string>("");
  const [startTime, setStartTime] = useState<Dayjs | null>();
  const [endTime, setEndTime] = useState<Dayjs | null>();

  const [editId, setEditId] = useState<string>('');
  const [detail, setDetail] = useState<SaleItemState | null>(null);

  const curOrg = useSelector((state: RootState) => state.orgReducer.curOrg);


  const { passed } = usePassed({
    setId: setEditId,
    updateApi: updateByKey,
    handleOk: id => rmItem(id),
    errorTip: t('sale.passError')
  })

  useEffect(() => {
    fetch(0);
  }, []);

  const columns: TableProps<SaleItemState>["columns"] = [
    {
      title: <SText text={t("sale.orderNumber")} />,
      key: "orderNumber",
      width: 200,
      fixed: "left",
      render: (_, item) => (
        <SText key={item.id + "number"} text={item.order?.number ?? ""} />
      ),
    },
    {
      title: <SText text={t("sale.innerNumber")} />,
      key: "orderInnerNumber",
      fixed: "left",
      width: 200,
      render: (_, item) => (
        <SText
          key={item.id + "innerNumber"}
          text={item.order?.innerNumber ?? ""}
        />
      ),
    },
    {
      title: t("sale.customerInfo"),
      children: [
        {
          title: <SText text={t("sale.code")} />,
          key: "customerCode",
          width: 100,
          render: (_, item) => (
            <SText
              key={item.id + "customerCode"}
              text={item.order?.code ?? ""}
            />
          ),
        },
        {
          title: <SText text={t("sale.shortName")} />,
          key: "customerShortName",
          width: 200,
          render: (_, item) => (
            <SText
              key={item.id + "customerShortName"}
              text={item.order?.shortName ?? ""}
            />
          ),
        },
      ],
    },
    {
      title: t("sale.proInfo"),
      children: [
        {
          title: <SText text={t("product.code")} />,
          key: "productCode",
          width: 120,
          render: (_, item) => (
            <SText
              key={item.id + "productCode"}
              text={item.product?.code ?? ""}
            />
          ),
        },
        {
          title: <SText text={t("product.name")} />,
          key: "productName",
          width: 180,
          render: (_, item) => (
            <SText
              key={item.id + "productCode"}
              text={item.product?.name ?? ""}
            />
          ),
        },
      ],
    },
    {
      title: <SText text={t("sale.quantity")} />,
      key: "quantity",
      width: 80,
      render: (_, item) => (
        <SText
          key={item.id + "quantity"}
          text={item.quantity.toString() ?? ""}
        />
      ),
    },
    {
      title: <SText text={t("sale.reRemark")} />,
      key: "reRemark",
      width: 300,
      render: (_, item) => (
        <SText key={item.id + "reRemark"} text={item.remark ?? ""} />
      ),
    },
    {
      title: <SText text={t("sale.customerModel")} />,
      key: "customerModel",
      width: 140,
      render: (_, item) => (
        <SText
          key={item.id + "customerModel"}
          text={item?.order?.customerModel ?? ""}
        />
      ),
    },
    {
      title: <SText text={t("sale.mfgr")} />,
      key: "mfgr",
      width: 140,
      render: (_, item) => (
        <SText key={item.id + "mfgr"} text={item?.order?.mfgr ?? ""} />
      ),
    },
    {
      title: <SText text={t("sale.reqDeliDate")} />,
      key: "reqDeliDate",
      width: 110,
      render: (_, item) => item?.order?.reqDeliDate ? dateFormat(dayjs(item.order.reqDeliDate)) : "",
    },
    {
      title: <SText text={t("sale.smtStartDate")} />,
      key: "smtStartDate",
      width: 110,
      render: (_, item) => item?.order?.smtStartDate ? dateFormat(dayjs(item.order.smtStartDate)) : "",
    },
    {
      title: <SText text={t("sale.preFinishDate")} />,
      key: "preFinishDate",
      width: 110,
      render: (_, item) => item?.order?.preFinishDate ? dateFormat(dayjs(item.order.preFinishDate)) : "",
    },
    {
      title: <SText text={t("sale.urgencyLevel")} />,
      key: "urgencyLevel",
      width: 110,
      render: (_, item) => (
        <SText key={item.id + "urgencyLevel"} text={item?.order?.urgencyLevel ?? ""} />
      ),
    },
    {
      title: <SText text={t("sale.specialReq")} />,
      key: "specialReq",
      width: 200,
      render: (_, item) => (
        <SText key={item.id + "specialReq"} text={item?.order?.specialReq ?? ""} />
      ),
    },
    {
      title: t("common.opt"),
      dataIndex: "operation",
      width: 120,
      fixed: "right",
      render: (val, record) => (
        <Flex justify="space-between">
          <Button type="primary" onClick={() => setDetail(record)}>
            {t("common.edit")}
          </Button>
          <Button
            type="primary"
            loading={updateLoading && editId === record.id}
            onClick={() => passed({ ...record.order, items: [] })}
          >
            {t("common.pass")}
          </Button>
        </Flex>
      ),
    },
  ];

  const fetch = (offset: number) => {
    const take = tableRef.current?.pageSize ?? DEFAULT_PAGE_SIZE
    fetchList({
      params: {
        body: { customerId, keyword },
        pagination: { offset, take },
        startTime: startTime ? startTime.startOf('day').valueOf() : undefined,
        endTime: endTime ? endTime.endOf('day').valueOf() : undefined,
      },
      state: CommonStateEnum.UN_ACTIVE,
      orgId: curOrg.id
    }).catch((err) => {
      switch (err) {
        case 10010:
          break;
        default:
          break;
      }
    });
  };

  const handleSearch = () => {
    fetch(0);
    tableRef?.current?.resetTablePage();
  };

  const headerChildren = (
    <Flex gap={10} style={{ width: "100%", marginBottom: 20 }} justify="end">
      <SelectCustomer onChange={setCustomerId} allowClear />
      <RangePicker size='small' onChange={val => {
        if (val) {
          setStartTime(val[0])
          setEndTime(val[1])
        } else {
          setStartTime(null)
          setEndTime(null)
        }
      }} />
      <Tooltip title={t("sale.dataReportSearch")}>
        <Input
          size='small'
          placeholder={t("sale.dataReportSearch")}
          style={{ width: 200 }}
          value={keyword}
          onChange={(e) => setKeyword(e.target.value.trim())}
          onPressEnter={handleSearch}
        />
      </Tooltip>
      <Button type='primary' icon={<SearchOutlined />} onClick={handleSearch} />
    </Flex>
  )

  return (
    <Flex vertical style={{ width: '100%', height: '100%' }}>
      <STableLayout
        loading={listLoading}
        dataSource={list}
        columns={columns as ColumnsType<AnyObject>}
        scrollX={2000}
        headerChildren={headerChildren}
        tableLayoutRef={tableRef}
        total={total}
        handleChange={offset => fetch(offset)}
      />
      {detail && <OrderForm
        key={detail?.id}
        handleOk={(data, item, itemId) => {
          const promiseApis: Array<Promise<any>> = [];
          const needEditOrder = !isEqualVal<SaleOrderAdd>(detail.order as unknown as SaleOrderAdd, data, ["innerNumber", "receiver", "receiverMobile", "receiverContact", "receiverAddress", "country", "taxRate", "shippingMethod", "preFinishDate", "smtStartDate", "reqDeliDate",]);
          const needEditProduct = !isEqualVal<SaleOrderAddItem>(detail as unknown as SaleOrderAddItem, item, ["priceHisId", "quantity", "remark"]);
          if (needEditOrder) {
            promiseApis.push(update(detail?.orderId ?? "", data));
          }
          if (needEditProduct) {
            promiseApis.push(updateSaleItem(itemId, item));
          }
          return Promise.allSettled(promiseApis)
            .then((res) => {
              let description = "";
              let message = t("common.editError");
              const map: {
                [key in number]: string;
              } = {
                10010: t("common.networkError"),
                71001: t("sale.codeExisted"),
                71005: t("sale.productNotExisted"),
              };
              if (needEditOrder && needEditProduct) {
                if (res[0].status === "rejected" && res[1].status === "rejected") {
                  description = `${map[res[0].reason]}, ${map[res[1].reason]
                    }`;
                } else if (res[0].status === "rejected" && res[1].status === "fulfilled") {
                  message = t("sale.projectSuccessOrderError");
                  description = `${map[res[0].reason]}`;
                  updateItem({ ...detail, ...item });
                } else if (res[0].status === "fulfilled" && res[1].status === "rejected") {
                  message = t("sale.orderSuccessProjectError");
                  description = `${map[res[1].reason]}`;
                  updateItem({
                    ...detail,
                    order: {
                      ...detail.order,
                      innerNumber: data.innerNumber,
                      receiver: data.receiver,
                      receiverAddress: data.receiverAddress,
                      receiverContact: data.receiverContact,
                      receiverMobile: data.receiverMobile,
                      country: data.country,
                      extPrice: data.extPrice,
                      taxRate: data.taxRate,
                    },
                  });
                }
              } else if (needEditOrder || needEditProduct) {
                if (res[0].status === "rejected") {
                  description = map[res[0].reason];
                } else {
                  if (needEditOrder) {
                    updateItem({
                      ...detail,
                      order: {
                        ...detail.order,
                        innerNumber: data.innerNumber,
                        receiver: data.receiver,
                        receiverAddress: data.receiverAddress,
                        receiverContact: data.receiverContact,
                        receiverMobile: data.receiverMobile,
                        country: data.country,
                        extPrice: data.extPrice,
                        taxRate: data.taxRate,
                      },
                    });
                  } else {
                    updateItem({ ...detail, ...item });
                  }
                }
              }

              if (description) {
                notification.error({ placement: 'top', message, description });
              } else {
                setDetail(null);
              }
            })
            .catch((err) => {
              console.error(err);
            });
        }}
        open={!!detail}
        confirmLoading={updateSaleLoading && updateItemLoading}
        handleCancel={() => setDetail(null)}
        leftTitle={t("common.edit")}
        data={detail}
        disabledItems={[
          'innerNumber',
          'receiver',
          'receiverContact',
          'receiverMobile',
          'receiverAddress',
          'country',
          'extPrice',
          'taxRate',
          'shippingMethod',
          'itemPriceHis',
          'quantity',
          'bquantity',
          'discountedPrice',
          'discount',
          'bdiscountedPrice',
          'bdiscount',
          'mfgr',
          'customerModel',
          'reqDeliDate'
        ]}
      />}
    </Flex>
  );
};

export default DataReviews;
